import { Dispatch, SetStateAction, SyntheticEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, FormControlLabel, Switch, Tabs, Typography } from '@mui/material';
import { Person, Notifications, Lock } from '@mui/icons-material';
import { ISearch, Loader, SimpleTable, TabPanel } from '@shared';
import { userColumns, courseColumns, examColumns, getCountPages, auditColumns } from '../helpers';
import { TabLabel, SearchTab, UserCell, CourseCell, ExamCell, AuditCell } from '.';

type TypeProps = {
  page: number;
  isLoading: boolean;
  isInitLoad: boolean;
  isNoFound: boolean;
  data: ISearch;
  partners?: { label: string; id: string }[];
  isLeadAuditor: boolean;
  isMultiplePartners: boolean;
  tabIndex: number;
  handleChange: (_: SyntheticEvent, newValue: number) => void;
  offset: number;
  setOffset: Dispatch<SetStateAction<number>>;
  searchText: string;
  isInterest: boolean;
  setIsInterest: Dispatch<SetStateAction<boolean>>;
  closeModal: () => void;
};

export const SearchTable = ({
  searchText,
  page,
  tabIndex,
  handleChange,
  isLoading,
  isInitLoad,
  data,
  partners,
  isLeadAuditor,
  isMultiplePartners,
  isNoFound,
  offset,
  setOffset,
  isInterest,
  setIsInterest,
  closeModal,
}: TypeProps) => {
  const { pathname: currentPathname } = useLocation();
  const tableProps = { page, noBorders: true, noParams: true, onPageChange: setOffset };
  const getCount = (value: number) => (searchText.length > 2 ? value : 0);

  return (
    <Box>
      <Tabs value={tabIndex} onChange={handleChange} aria-label='search-tabs'>
        <SearchTab
          label={
            <TabLabel label='Users' count={getCount(data.usersCount)} isActive={tabIndex === 0} />
          }
          iconPosition='start'
          icon={<Person />}
        />
        <SearchTab
          label={
            <TabLabel
              label='Courses'
              count={getCount(data.coursesCount)}
              isActive={tabIndex === 1}
            />
          }
          iconPosition='start'
          icon={<Notifications />}
        />
        <SearchTab
          label={
            <TabLabel label='Exams' count={getCount(data.examsCount)} isActive={tabIndex === 2} />
          }
          icon={<Lock />}
          iconPosition='start'
        />
        {isLeadAuditor && (
          <SearchTab
            label={
              <TabLabel
                label='Audits'
                count={getCount(data.auditsCount)}
                isActive={tabIndex === 3}
              />
            }
            icon={<Notifications />}
            iconPosition='start'
          />
        )}
      </Tabs>
      {tabIndex === 0 && (
        <Box
          marginTop={3}
          paddingX={1.5}
          display='flex'
          width='fit-content'
          alignItems='center'
          sx={{ backgroundColor: '#F5F5F5', borderRadius: 1 }}
        >
          <Typography>Filters:</Typography>
          <FormControlLabel
            value='start'
            checked={isInterest}
            control={<Switch color='primary' />}
            label='Audit of Interest'
            labelPlacement='start'
            onChange={(_, checked) => {
              if (offset !== 1) setOffset(1);
              setIsInterest(checked);
            }}
          />
        </Box>
      )}
      {searchText.length > 2 && !isInitLoad && !isNoFound && (
        <>
          <TabPanel value={tabIndex} index={0} noBorder>
            <SimpleTable
              {...tableProps}
              entityName='User'
              columns={
                !isMultiplePartners
                  ? userColumns.filter((i) => i.id !== 'partnerUuid')
                  : userColumns
              }
              rows={data.users}
              renderElems={({ uuid, sessionUuid, auditOfInterest, partnerUuid, ...rest }) => {
                return (
                  <UserCell
                    partners={partners}
                    isMultiplePartners={isMultiplePartners}
                    entries={Object.entries({
                      ...(isMultiplePartners && { partnerUuid }),
                      ...rest,
                    })}
                    sessionId={sessionUuid}
                    isAuditOfInterest={auditOfInterest}
                    isView={
                      rest.sessionStatus === 'No Auditing' ||
                      rest.sessionStatus === 'Done' ||
                      rest.sessionStatus === 'Capturing'
                    }
                    pathname={currentPathname}
                    closeModal={closeModal}
                    key={uuid}
                  />
                );
              }}
              pagination={getCountPages(data.usersCount) > 1}
              totalPages={getCountPages(data.usersCount)}
            />
          </TabPanel>
          <TabPanel value={tabIndex} index={1} noBorder>
            <SimpleTable
              {...tableProps}
              entityName='Course'
              columns={
                !isMultiplePartners
                  ? courseColumns.filter((i) => i.id !== 'partnerUuid')
                  : courseColumns
              }
              rows={data.courses}
              renderElems={(rowData) => {
                return (
                  <CourseCell
                    item={rowData}
                    partners={partners}
                    isMultiplePartners={isMultiplePartners}
                    pathname={currentPathname}
                    closeModal={closeModal}
                  />
                );
              }}
              pagination={getCountPages(data.coursesCount) > 1}
              totalPages={getCountPages(data.coursesCount)}
            />
          </TabPanel>
          <TabPanel value={tabIndex} index={2} noBorder>
            <SimpleTable
              {...tableProps}
              entityName='Exam'
              columns={
                !isMultiplePartners
                  ? examColumns.filter((i) => i.id !== 'partnerUuid')
                  : examColumns
              }
              rows={data.exams}
              renderElems={(rowData) => {
                return (
                  <ExamCell
                    item={rowData}
                    partners={partners}
                    isMultiplePartners={isMultiplePartners}
                    pathname={currentPathname}
                    closeModal={closeModal}
                  />
                );
              }}
              pagination={getCountPages(data.examsCount) > 1}
              totalPages={getCountPages(data.examsCount)}
            />
          </TabPanel>
          <TabPanel value={tabIndex} index={3} noBorder>
            <SimpleTable
              {...tableProps}
              entityName='Audit'
              columns={auditColumns}
              rows={data.audits}
              renderElems={(rowData) => {
                return (
                  <AuditCell item={rowData} pathname={currentPathname} closeModal={closeModal} />
                );
              }}
              pagination={getCountPages(data.auditsCount) > 1}
              totalPages={getCountPages(data.auditsCount)}
            />
          </TabPanel>
        </>
      )}
      {(isInitLoad || isLoading) && <Loader />}
    </Box>
  );
};
